import { forwardRef, useId, useRef, useState, type AriaAttributes } from 'react'
import clsx from 'clsx'
import { Check, ChevronDown } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/popover/Popover'
import type { ColumnTag } from '../schemas'
import TableTag from './TableTag'

type TagFilterPickerProps = Pick<AriaAttributes, 'aria-invalid' | 'aria-describedby'> & {
  tags: ColumnTag[]
  selected: string[]
  onChange: (selected: string[]) => void
  ariaLabel: string
  className?: string
  defaultOpen?: boolean
}

/** Selections belong to the filter draft; closing the picker does not apply the table query. */
const TagFilterPicker = forwardRef<HTMLInputElement, TagFilterPickerProps>(function TagFilterPicker(
  { tags, selected, onChange, ariaLabel, className, defaultOpen = false, ...ariaProps },
  ref
) {
  const [open, setOpen] = useState(defaultOpen)
  const [query, setQuery] = useState('')
  const [activeLabel, setActiveLabel] = useState<string | null>(null)
  const input = useRef<HTMLInputElement | null>(null)
  const anchor = useRef<HTMLLabelElement>(null)
  const id = useId()
  const search = query.trim().toLocaleLowerCase()
  const filtered = tags.filter((tag) => tag.label.toLocaleLowerCase().startsWith(search))
  const activeIndex = filtered.findIndex((tag) => tag.label === activeLabel)
  const selectedTags = tags.filter((tag) => selected.includes(tag.label))
  const changeOpen = (next: boolean) => {
    setOpen(next)
    if (!next) {
      setQuery('')
      setActiveLabel(null)
    }
  }
  const finish = () => {
    input.current?.focus()
    changeOpen(false)
  }
  const toggle = (label: string) => {
    onChange(selected.includes(label) ? selected.filter((value) => value !== label) : [...selected, label])
    setQuery('')
    setActiveLabel(null)
    input.current?.focus()
  }

  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverAnchor asChild>
        <label
          ref={anchor}
          htmlFor={`${id}-input`}
          className={clsx(
            'flex min-h-10 w-full min-w-0 cursor-text items-center justify-between gap-2 rounded border border-brand-neutral-300 bg-white px-3 py-1.5 text-left text-sm focus-within:ring-2 focus-within:ring-inset focus-within:ring-brand-400 hover:ring-2 hover:ring-inset hover:ring-brand-400',
            className
          )}
        >
          <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
            {selectedTags.map((tag) => (
              <span key={tag.label} className="min-w-0 max-w-full">
                <TableTag tag={tag}>
                  <span className="break-words text-left">{tag.label}</span>
                </TableTag>
              </span>
            ))}
            <input
              ref={(element) => {
                input.current = element
                if (typeof ref === 'function') ref(element)
                else if (ref) ref.current = element
              }}
              {...ariaProps}
              id={`${id}-input`}
              role="combobox"
              aria-label={ariaLabel}
              aria-autocomplete="list"
              aria-expanded={open}
              aria-controls={open ? `${id}-options` : undefined}
              aria-activedescendant={open && activeIndex >= 0 ? `${id}-option-${activeIndex}` : undefined}
              autoComplete="off"
              placeholder={selectedTags.length ? '' : 'Select tags…'}
              className="h-6 w-16 min-w-12 flex-1 border-0 bg-transparent p-0 text-sm text-brand-neutral-950 outline-none placeholder:text-brand-neutral-600 focus:ring-0"
              value={query}
              onFocus={() => setOpen(true)}
              onClick={() => setOpen(true)}
              onChange={(event) => {
                setQuery(event.target.value)
                setActiveLabel(null)
                setOpen(true)
              }}
              onKeyDown={(event) => {
                if (event.nativeEvent.isComposing) return
                if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                  event.preventDefault()
                  setOpen(true)
                  if (!filtered.length) return
                  const next = event.key === 'ArrowDown' ? (activeIndex + 1) % filtered.length : (activeIndex <= 0 ? filtered.length : activeIndex) - 1
                  setActiveLabel(filtered[next]?.label ?? null)
                  document.getElementById(`${id}-option-${next}`)?.scrollIntoView({ block: 'nearest' })
                } else if (event.key === 'Enter') {
                  event.preventDefault()
                  if (!open) setOpen(true)
                  else {
                    const tag = filtered[activeIndex >= 0 ? activeIndex : 0]
                    if (tag) toggle(tag.label)
                  }
                }
              }}
            />
          </span>
          <ChevronDown aria-hidden className="size-4 shrink-0 text-brand-neutral-400" />
        </label>
      </PopoverAnchor>
      <PopoverContent
        aria-label={`Tags for ${ariaLabel}`}
        align="start"
        width="auto"
        showArrow={false}
        sideOffset={6}
        collisionPadding={8}
        className="z-[1001] flex w-[max(240px,var(--radix-popover-trigger-width))] max-w-[calc(100vw-16px)] max-h-[var(--radix-popover-content-available-height)] flex-col overflow-hidden border border-brand-neutral-200 p-0"
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          input.current?.focus()
        }}
        onCloseAutoFocus={(event) => event.preventDefault()}
        onInteractOutside={(event) => {
          if (event.target instanceof Node && anchor.current?.contains(event.target)) event.preventDefault()
        }}
        onEscapeKeyDown={(event) => {
          event.preventDefault()
          finish()
        }}
      >
        <div id={`${id}-options`} role="listbox" aria-label={`Tags for ${ariaLabel}`} aria-multiselectable className="min-h-0 max-h-64 overflow-y-auto p-1.5">
          {filtered.map((tag, index) => (
            <Button
              key={tag.label}
              id={`${id}-option-${index}`}
              type="button"
              role="option"
              aria-label={tag.label}
              aria-selected={selected.includes(tag.label)}
              variant="plain"
              color="neutral"
              size="sm"
              tabIndex={-1}
              className={clsx(
                '!h-auto min-h-9 w-full !justify-start !gap-2.5 !px-2 !py-1.5',
                (selected.includes(tag.label) || activeIndex === index) && '!bg-brand-100/50'
              )}
              onClick={() => toggle(tag.label)}
            >
              <span
                aria-hidden
                className={clsx(
                  'flex size-4 shrink-0 items-center justify-center rounded border',
                  selected.includes(tag.label) ? 'border-brand-500 bg-brand-500 text-white' : 'border-brand-neutral-300 bg-white'
                )}
              >
                {selected.includes(tag.label) && <Check className="size-3" />}
              </span>
              <TableTag tag={tag}>
                <span className="text-left">{tag.label}</span>
              </TableTag>
            </Button>
          ))}
          {!filtered.length && <p className="px-2 py-2 text-xs text-brand-neutral-500">{search ? 'No matching tags' : 'No tags yet'}</p>}
        </div>
        <div className="flex shrink-0 items-center justify-between gap-2 border-t border-brand-neutral-200 px-3.5 py-2">
          <span role="status" className="text-xs text-brand-neutral-600">
            {selected.length} selected
          </span>
          <Button type="button" size="sm" onClick={finish}>
            Done
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
})

export default TagFilterPicker
